import * as React from 'react';
import { useTheme } from '../../theme';
import { CSSRulesFunction, ResponsiveValue } from '../../theme/types';
import Box, { BoxProps } from '../Box';

interface AppearanceProps {
  ratio?: ResponsiveValue<number>;
  objectPosition?: ResponsiveValue<string>;
}

export interface AspectRatioProps extends AppearanceProps, BoxProps {}

const defaultRatio = 4 / 3;
const defaultObjectPosition = 'center';

export const aspectRatioStyle: CSSRulesFunction<AppearanceProps> = (
  theme,
  props
) => {
  const { ratio = defaultRatio } = props;
  const paddingBottom = theme.responsive(ratio, r => ({
    paddingBottom: `${(1 / r) * 100}%`,
  }));

  const objectPosition = theme.responsive(
    props.objectPosition || defaultObjectPosition,
    pos => ({
      '& > img, & > video': {
        objectPosition: pos,
      },
    })
  );

  return [
    {
      position: 'relative',
      '&:before': {
        height: 0,
        content: `""`,
        display: 'block',
      },
      '& > *': {
        overflow: 'hidden',
        position: 'absolute',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      },
      '& > img, & > video': {
        objectFit: 'cover',
      },
    },
    {
      '&:before': paddingBottom,
    },
    objectPosition,
  ];
};

const AspectRatio = React.memo(
  React.forwardRef<HTMLDivElement, AspectRatioProps>((props, ref) => {
    const { ratio, children, ...rest } = props;
    const child = React.Children.only(children);
    const { theme } = useTheme();

    return (
      <Box ref={ref} css={aspectRatioStyle(theme, props)} {...rest}>
        {child}
      </Box>
    );
  })
);

AspectRatio.displayName = 'AspectRatio';

export default AspectRatio;
